import { Hash, PenLine, Sparkles, ArrowRight, Home } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { scrollToId } from "../lib/smooth";
import { PARTNER } from "../data/site";

const Card = ({ icon: Icon, title, tagline, items, img, cta, onBook, testid, className }) => (
    <div
        data-testid={testid}
        className={`group relative rounded-2xl border border-white/10 bg-[#0E1B33] overflow-hidden hover:-translate-y-1 hover:shadow-[0_0_50px_rgba(201,169,98,0.12)] hover:border-[#C9A962]/30 transition-all duration-500 ${className}`}
    >
        {img && (
            <div className="absolute inset-0 opacity-[0.12] group-hover:opacity-[0.2] transition-opacity duration-700">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E1B33] via-[#0E1B33]/70 to-transparent" />
            </div>
        )}
        <div className="relative p-8 lg:p-10 h-full flex flex-col">
            <div className="w-12 h-12 rounded-full border border-[#C9A962]/40 flex items-center justify-center text-[#C9A962] mb-6 group-hover:bg-[#C9A962]/10 transition-colors">
                <Icon size={20} />
            </div>
            <h3 className="font-display text-2xl lg:text-3xl font-semibold text-[#F0EBE0]">{title}</h3>
            <p className="mt-2 text-sm font-light text-[#A19D94]">{tagline}</p>
            <ul className="mt-6 space-y-2.5 flex-1">
                {items.map((it) => (
                    <li key={it} className="flex items-center gap-3 text-sm text-[#A19D94]">
                        <span className="w-1 h-1 rounded-full bg-[#C9A962]" /> {it}
                    </li>
                ))}
            </ul>
            <div className="mt-8 flex items-center gap-6">
                <button
                    data-testid={`${testid}-learn-more`}
                    onClick={() => scrollToId("#pricing")}
                    className="text-xs uppercase tracking-[0.2em] text-[#A19D94] hover:text-[#F0EBE0] transition-colors flex items-center gap-2"
                >
                    Learn More <ArrowRight size={14} />
                </button>
                <button
                    data-testid={`${testid}-book-now`}
                    onClick={onBook}
                    className="text-xs uppercase tracking-[0.2em] text-[#C9A962] border-b border-[#C9A962]/40 pb-0.5 hover:border-[#C9A962] transition-colors"
                >
                    Book Now
                </button>
            </div>
        </div>
    </div>
);

export const Services = ({ onBook }) => {
    return (
        <section id="services" data-testid="services-section" className="py-28 lg:py-36 px-6">
            <div className="max-w-7xl mx-auto">
                <Reveal className="max-w-2xl">
                    <Overline>Our Services</Overline>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F0EBE0] leading-tight">
                        What your <span className="italic text-[#C9A962]">numbers & ink</span> reveal
                    </h2>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
                    <Reveal className="md:col-span-7">
                        <Card
                            icon={Hash}
                            title="Numerology"
                            tagline="The mathematics of your destiny — decoded from your name and date of birth."
                            items={["Life Path Analysis", "Name Numerology & Correction", "Career & Business Numerology", "Relationship Compatibility", "Namekaran (Baby / Brand Naming)"]}
                            img="https://images.unsplash.com/photo-1513346940221-6f673d962e97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                            onBook={onBook}
                            testid="service-numerology"
                            className="h-full"
                        />
                    </Reveal>
                    <Reveal delay={0.12} className="md:col-span-5">
                        <Card
                            icon={PenLine}
                            title="Graphology"
                            tagline="Your handwriting is a mirror of your mind."
                            items={["Handwriting Analysis", "Signature Analysis", "Personality Analysis", "Doodle Analysis", "Child / Student Analysis"]}
                            img="https://images.pexels.com/photos/15876139/pexels-photo-15876139.jpeg?auto=compress&cs=tinysrgb&w=1200"
                            onBook={onBook}
                            testid="service-graphology"
                            className="h-full"
                        />
                    </Reveal>
                    <Reveal delay={0.2} className="md:col-span-12">
                        <Card
                            icon={Sparkles}
                            title="Combined Reading & Healing"
                            tagline="Numerology + Graphology woven into one personalized consultation — with remedies and healings included."
                            items={["Business / Employee & Logo Analysis", "Self-Development Sessions", "Crystal Remedies & Reiki", "Relationship Counselling", "Online Consultation Available Worldwide"]}
                            onBook={onBook}
                            testid="service-combined"
                            className="md:flex-row"
                        />
                    </Reveal>
                    <Reveal delay={0.22} className="md:col-span-6">
                        <div data-testid="service-reiki" className="group relative rounded-2xl border border-[#C9A962]/30 bg-[#0E1B33] overflow-hidden hover:-translate-y-1 hover:shadow-[0_0_50px_rgba(201,169,98,0.12)] hover:border-[#C9A962]/50 transition-all duration-500 h-full">
                            <span className="absolute top-5 right-5 px-4 py-1.5 rounded-full bg-[#C9A962] text-[#081226] text-[10px] font-bold uppercase tracking-[0.2em] z-10">Launching Soon</span>
                            <div className="relative p-8 lg:p-10 h-full flex flex-col">
                                <div className="w-12 h-12 rounded-full border border-[#C9A962]/40 flex items-center justify-center text-[#C9A962] mb-6 group-hover:bg-[#C9A962]/10 transition-colors">
                                    <Sparkles size={20} />
                                </div>
                                <h3 className="font-display text-2xl lg:text-3xl font-semibold text-[#F0EBE0]">Reiki Healing</h3>
                                <p className="mt-2 text-sm font-light text-[#A19D94]">In-studio energy healing — a new era of healing & transformation.</p>
                                <ul className="mt-6 space-y-2.5 flex-1">
                                    {["Deep Relaxation", "Energy Balancing", "Chakra & Reiki Healing Sessions", "Positive Vibes & Protection", "In-Studio at Mulund West"].map((it) => (
                                        <li key={it} className="flex items-center gap-3 text-sm text-[#A19D94]">
                                            <span className="w-1 h-1 rounded-full bg-[#C9A962]" /> {it}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-5 font-display italic text-sm text-[#C9A962]">Heal Deeply. Live Lightly. Rise Higher.</p>
                                <div className="mt-8 flex items-center gap-6">
                                    <button data-testid="service-reiki-book-now" onClick={onBook}
                                        className="text-xs uppercase tracking-[0.2em] text-[#C9A962] border-b border-[#C9A962]/40 pb-0.5 hover:border-[#C9A962] transition-colors">
                                        Book Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.25} className="md:col-span-6">
                        <div data-testid="service-vastu" className="group relative rounded-2xl border border-[#C9A962]/30 bg-[#0E1B33] overflow-hidden hover:-translate-y-1 hover:shadow-[0_0_50px_rgba(201,169,98,0.12)] hover:border-[#C9A962]/50 transition-all duration-500 h-full">
                            <span className="absolute top-5 right-5 px-4 py-1.5 rounded-full bg-[#C9A962] text-[#081226] text-[10px] font-bold uppercase tracking-[0.2em] z-10">New Partnership</span>
                            <div className="relative p-8 lg:p-10 h-full flex flex-col">
                                <div className="w-12 h-12 rounded-full border border-[#C9A962]/40 flex items-center justify-center text-[#C9A962] mb-6 group-hover:bg-[#C9A962]/10 transition-colors">
                                    <Home size={20} />
                                </div>
                                <h3 className="font-display text-2xl lg:text-3xl font-semibold text-[#F0EBE0]">Vastu Analysis</h3>
                                <p className="mt-2 text-sm font-light text-[#A19D94]">Ink of Destiny × Vastudeep — align your space with your success.</p>
                                <ul className="mt-6 space-y-2.5 flex-1">
                                    {["Vastu Analysis for Home & Office", "Energy Alignment", "Growth & Success Remedies"].map((it) => (
                                        <li key={it} className="flex items-center gap-3 text-sm text-[#A19D94]">
                                            <span className="w-1 h-1 rounded-full bg-[#C9A962]" /> {it}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-5 text-xs text-[#5C5A56]">
                                    with <span className="text-[#F0EBE0]">{PARTNER.name}</span> — {PARTNER.title}, {PARTNER.brand} · <a data-testid="vastu-partner-phone" href={`tel:+${PARTNER.phone}`} className="text-[#C9A962] hover:underline">{PARTNER.phoneDisplay}</a>
                                </p>
                                <p className="mt-3 font-display italic text-sm text-[#C9A962]">When mindset and environment work together, miracles happen.</p>
                                <div className="mt-6 flex items-center gap-6">
                                    <button data-testid="service-vastu-book-now" onClick={onBook}
                                        className="text-xs uppercase tracking-[0.2em] text-[#C9A962] border-b border-[#C9A962]/40 pb-0.5 hover:border-[#C9A962] transition-colors">
                                        Book Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
